import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  Input,
  FormGroup,
  Badge,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Alert,
} from "reactstrap";
import classnames from "classnames";
import { AlternativePagination } from "../../components/Common/Table";
import AWSS3Uploader from "../../components/Common/S3uploader";
import "../../global.css";
import {
  GetStoreMenu,
  AssignProductToStore,
  CloneStoreMenu,
  UpdateStoreProduct,
  RemoveProductFromStore,
  SaveStoreMenuOrder,
  GetProducts,
  GetCategories,
  GetStores,
} from "../../requests";
import LoadingSpinner from "../../components/LoadingSpinner";
import { toast } from 'react-toastify';
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableRow = ({
  row,
  index,
  totalLength,
  onEdit,
  onRemove,
  onMoveUp,
  onMoveDown,
  getCategoryName,
  isFiltered,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.productId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    backgroundColor: isDragging ? "#fcfcfc" : undefined,
    boxShadow: isDragging ? "0 5px 15px rgba(0,0,0,0.08)" : undefined,
    zIndex: isDragging ? 1010 : undefined,
  };

  return (
    <tr ref={setNodeRef} style={style} className={classnames({ "table-active": isDragging })}>
      <td style={{ width: "80px", verticalAlign: "middle" }} className="text-center">
        {!isFiltered ? (
          <div className="d-flex align-items-center justify-content-center gap-1">
            <span
              {...attributes}
              {...listeners}
              style={{ cursor: "grab", padding: "4px 8px" }}
              className="text-muted fs-16 drag-handle"
              title="Drag to reorder"
            >
              ☰
            </span>
            <div className="d-flex flex-column align-items-center">
              <button
                type="button"
                className="btn btn-link btn-sm p-0 lh-1 text-primary"
                onClick={() => onMoveUp(index)}
                disabled={index === 0}
                style={{ fontSize: "10px" }}
                title="Move Up"
              >
                ▲
              </button>
              <button
                type="button"
                className="btn btn-link btn-sm p-0 lh-1 text-primary"
                onClick={() => onMoveDown(index)}
                disabled={index === totalLength - 1}
                style={{ fontSize: "10px" }}
                title="Move Down"
              >
                ▼
              </button>
            </div>
          </div>
        ) : (
          <span className="text-muted fs-12">-</span>
        )}
      </td>
      <td style={{ width: "60px", verticalAlign: "middle" }} className="text-center">
        {row.displayOrder || index + 1}
      </td>
      <td style={{ width: "80px", verticalAlign: "middle" }}>
        {row.productDetails?.image ? (
          <img
            src={row.productDetails.image}
            alt={row.productDetails.name}
            width="50"
            height="50"
            style={{ objectFit: "cover", borderRadius: "4px" }}
          />
        ) : (
          <div
            className="bg-light d-flex align-items-center justify-content-center"
            style={{ width: 50, height: 50, borderRadius: "4px" }}
          >
            <i className="mdi mdi-pizza-thin text-muted" style={{ fontSize: "20px" }}></i>
          </div>
        )}
      </td>
      <td style={{ verticalAlign: "middle" }} className="font-weight-semibold">
        {row.productDetails?.name || row.productId}
      </td>
      <td style={{ width: "120px", verticalAlign: "middle" }}>
        {getCategoryName(row.productDetails?.categoryId)}
      </td>
      <td style={{ width: "250px", verticalAlign: "middle" }}>
        <div className="py-1">
          {(row.variants || []).map((v, idx) => (
            <div key={idx} className="fs-11 mb-1">
              <strong>{v.variantId}</strong>:{" "}
              {v.discountPrice ? (
                <>
                  ₹{v.discountPrice}{" "}
                  <span className="text-muted">
                    <del>₹{v.price}</del>
                  </span>
                </>
              ) : (
                <>₹{v.price}</>
              )}
              <Badge
                color={
                  v.availability === "available"
                    ? "primary"
                    : "danger"
                }
                className="ms-1"
              >
                {v.availability === "available"
                  ? "In Stock"
                  : v.availability === "out_of_stock"
                    ? "Out of Stock"
                    : "Unavailable"}
              </Badge>
            </div>
          ))}
        </div>
      </td>
      <td style={{ width: "90px", verticalAlign: "middle" }}>
        {row.featured ? (
          <Badge color="soft-warning" className="badge-soft-warning">
            Featured
          </Badge>
        ) : (
          <span className="text-muted fs-12">No</span>
        )}
      </td>
      <td style={{ width: "90px", verticalAlign: "middle" }}>
        {row.enabled ? (
          <Badge color="success" className="badge-success">
            Enabled
          </Badge>
        ) : (
          <Badge color="secondary" className="badge-secondary">
            Disabled
          </Badge>
        )}
      </td>
      <td style={{ width: "150px", verticalAlign: "middle" }}>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-outline-primary" onClick={() => onEdit(row)}>
            Edit
          </button>
          <button className="btn btn-sm btn-outline-danger" onClick={() => onRemove(row)}>
            Remove
          </button>
        </div>
      </td>
    </tr>
  );
};

const StoreMenu = () => {
  document.title = "Store Menu | Susipizza";

  const userRole = localStorage.getItem("role") || "admin";
  const userStoreId = localStorage.getItem("storeId") || "";
  const userStoreName = localStorage.getItem("storeName") || "";

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("1"); // 1 = Assigned Menu, 2 = Product Catalog

  // Data Lists
  const [assignedMenu, setAssignedMenu] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stores, setStores] = useState([]);

  // Store Selector
  const [selectedStoreId, setSelectedStoreId] = useState(userRole === "superadmin" ? "" : userStoreId);

  // Filters for Assigned Tab
  const [assignedSearch, setAssignedSearch] = useState("");
  const [assignedCategory, setAssignedCategory] = useState("");
  const [assignedVeg, setAssignedVeg] = useState("");

  // Filters for Catalog Tab
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogCategory, setCatalogCategory] = useState("");
  const [catalogVeg, setCatalogVeg] = useState("");

  // Modal configuration state
  const [modal, setModal] = useState(false);
  const [modalProduct, setModalProduct] = useState(null); // original product from master
  const [isEditing, setIsEditing] = useState(false); // edit existing vs assign new

  // Modal fields
  const [enabled, setEnabled] = useState(true);
  const [featured, setFeatured] = useState(false);
  const [recommended, setRecommended] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [displayOrder, setDisplayOrder] = useState("0");
  const [variantsForm, setVariantsForm] = useState([]); // Array: [{ variantId, price, discountPrice, availability, sku, image, calories }]

  // Confirm delete modal
  const [confirmModal, setConfirmModal] = useState(false);
  const [targetProduct, setTargetProduct] = useState(null);

  // "Copy from another store" — lets an admin reuse the price/discount/SKU/
  // calories/stock already entered for this same product at a different
  // store, instead of retyping them every time the same catalog item is
  // assigned store-by-store.
  const [copySourceStoreId, setCopySourceStoreId] = useState("");
  const [copyLoading, setCopyLoading] = useState(false);

  // "Clone entire store menu" — the bulk counterpart to the per-item copy
  // above, for standing up a brand-new store's whole menu from an existing
  // one in one action instead of assigning dozens of products by hand.
  const [cloneModal, setCloneModal] = useState(false);
  const [cloneSourceStoreId, setCloneSourceStoreId] = useState("");
  const [cloneLoading, setCloneLoading] = useState(false);
  const [cloneResult, setCloneResult] = useState(null);

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const toggleModal = () => {
    if (modal) {
      setModalProduct(null);
      setIsEditing(false);
      setEnabled(true);
      setFeatured(false);
      setDisplayOrder("0");
      setVariantsForm([]);
      setCopySourceStoreId("");
    }
    setModal(!modal);
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [catResp, storeResp] = await Promise.all([
        GetCategories(),
        GetStores(),
      ]);
      setCategories(catResp.data.data || catResp.data || []);
      const fetchedStores = storeResp.data.data || storeResp.data || [];
      setStores(fetchedStores);

      // If superadmin, select first store as default
      if (userRole === "superadmin" && fetchedStores.length > 0) {
        setSelectedStoreId(fetchedStores[0].id);
      }
    } catch (err) {
      console.error("Error loading stores/categories:", err);
      toast.error("Failed to load initial configuration");
    } finally {
      setLoading(false);
    }
  };

  const loadMenuAndCatalog = async () => {
    if (!selectedStoreId) return;
    try {
      setLoading(true);
      // Fetch assigned items for the store
      const menuResp = await GetStoreMenu(selectedStoreId);
      setAssignedMenu(menuResp.data.data || menuResp.data || []);

      // Fetch Catalog items
      const catalogFilters = {
        search: catalogSearch,
        categoryId: catalogCategory,
        veg: catalogVeg,
        status: "active", // Only active products from Master Catalog can be assigned
      };
      const catResp = await GetProducts(catalogFilters);
      setCatalogProducts(catResp.data.data || catResp.data || []);
    } catch (err) {
      console.error("Error loading menu/catalog:", err);
      toast.error("Failed to load menu data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadMenuAndCatalog();
  }, [selectedStoreId, catalogCategory, catalogVeg]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = assignedMenu.findIndex((item) => item.productId === active.id);
    const newIndex = assignedMenu.findIndex((item) => item.productId === over.id);

    const updatedList = arrayMove(assignedMenu, oldIndex, newIndex);
    setAssignedMenu(updatedList);
    await saveNewOrder(updatedList);
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const updatedList = [...assignedMenu];
    const temp = updatedList[index];
    updatedList[index] = updatedList[index - 1];
    updatedList[index - 1] = temp;
    setAssignedMenu(updatedList);
    await saveNewOrder(updatedList);
  };

  const handleMoveDown = async (index) => {
    if (index === assignedMenu.length - 1) return;
    const updatedList = [...assignedMenu];
    const temp = updatedList[index];
    updatedList[index] = updatedList[index + 1];
    updatedList[index + 1] = temp;
    setAssignedMenu(updatedList);
    await saveNewOrder(updatedList);
  };

  const saveNewOrder = async (updatedList) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const orderedIds = updatedList.map((item) => item.productId);
      await SaveStoreMenuOrder(token, selectedStoreId, orderedIds);
      toast.success("Store menu order updated successfully");
      loadMenuAndCatalog();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save store menu order");
      loadMenuAndCatalog();
    } finally {
      setLoading(false);
    }
  };

  const handleSearchAssigned = () => {
    loadMenuAndCatalog();
  };

  const handleSearchCatalog = () => {
    loadMenuAndCatalog();
  };

  // Open Assign Modal for a product
  const startAssign = (product) => {
    setModalProduct(product);
    setIsEditing(false);
    setEnabled(true);
    setFeatured(false);
    setRecommended(false);
    setIsNew(false);
    setDisplayOrder((assignedMenu.length + 1).toString());

    // Pre-populate variants
    if (product.variantEnabled && product.variantValueIds && product.variantValueIds.length > 0) {
      const form = product.variantValueIds.map(val => ({
        variantId: val,
        price: "",
        discountPrice: "",
        availability: "available",
        sku: "",
        image: "",
        calories: "",
        stockQuantity: ""
      }));
      setVariantsForm(form);
    } else {
      setVariantsForm([{
        variantId: "standard",
        price: "",
        discountPrice: "",
        availability: "available",
        sku: "",
        image: "",
        calories: "",
        stockQuantity: ""
      }]);
    }
    setModal(true);
  };

  // Open Edit Modal for an assigned store product
  const startEdit = (row) => {
    const origProduct = {
      productId: row.productId,
      name: row.productDetails?.name,
      variantEnabled: row.productDetails?.variantEnabled,
      variantValueIds: row.productDetails?.variantValueIds,
      description: row.productDetails?.description,
      image: row.productDetails?.image
    };

    setModalProduct(origProduct);
    setIsEditing(true);
    setEnabled(row.enabled !== false);
    setFeatured(row.featured === true);
    setRecommended(row.recommended === true);
    setIsNew(row.isNew === true);
    setDisplayOrder(row.displayOrder ? row.displayOrder.toString() : "0");

    const form = (row.variants || []).map(v => ({
      variantId: v.variantId,
      price: v.price.toString(),
      discountPrice: v.discountPrice !== null && v.discountPrice !== undefined ? v.discountPrice.toString() : "",
      availability: v.availability || "available",
      sku: v.sku || "",
      image: v.image || "",
      calories: v.calories !== null && v.calories !== undefined ? v.calories.toString() : "",
      stockQuantity: v.stockQuantity !== null && v.stockQuantity !== undefined ? v.stockQuantity.toString() : ""
    }));
    setVariantsForm(form);
    setModal(true);
  };

  const handleVariantPriceChange = (index, field, value) => {
    const updated = [...variantsForm];
    updated[index][field] = value;
    setVariantsForm(updated);
  };

  // Pulls this same product's existing price/discount/SKU/calories/stock
  // config from another store's menu and pre-fills the form with it —
  // matched by variantId so it only fills in variants this product
  // actually has here, rather than blindly overwriting the list.
  const handleCopyFromStore = async () => {
    if (!copySourceStoreId) {
      toast.error("Please select a store to copy from");
      return;
    }
    if (!modalProduct) return;

    const productId = modalProduct.productId || modalProduct.id;
    try {
      setCopyLoading(true);
      const menuResp = await GetStoreMenu(copySourceStoreId);
      const sourceMenu = menuResp.data.data || menuResp.data || [];
      const sourceItem = sourceMenu.find((item) => item.productId === productId);

      if (!sourceItem) {
        const sourceStoreName = stores.find((s) => s.id === copySourceStoreId)?.name || "the selected store";
        toast.error(`This product isn't assigned to ${sourceStoreName} — nothing to copy.`);
        return;
      }

      const sourceVariantsById = {};
      (sourceItem.variants || []).forEach((v) => {
        sourceVariantsById[v.variantId] = v;
      });

      const updated = variantsForm.map((v) => {
        const source = sourceVariantsById[v.variantId];
        if (!source) return v; // no matching variant on the source store — leave as-is
        return {
          ...v,
          price: source.price !== undefined && source.price !== null ? source.price.toString() : v.price,
          discountPrice: source.discountPrice !== undefined && source.discountPrice !== null ? source.discountPrice.toString() : "",
          availability: source.availability || v.availability,
          sku: source.sku || "",
          image: source.image || "",
          calories: source.calories !== undefined && source.calories !== null ? source.calories.toString() : "",
          stockQuantity: source.stockQuantity !== undefined && source.stockQuantity !== null ? source.stockQuantity.toString() : "",
        };
      });
      setVariantsForm(updated);
      toast.success("Copied pricing details — review and save when ready.");
    } catch (err) {
      console.error("Copy from store error:", err);
      toast.error("Failed to copy details from the selected store");
    } finally {
      setCopyLoading(false);
    }
  };

  const handleSave = async () => {
    // Validations
    if (!selectedStoreId) {
      toast.error("Please select a store");
      return;
    }

    // Price validations
    for (const v of variantsForm) {
      const p = parseFloat(v.price);
      if (isNaN(p) || p <= 0) {
        toast.error(`Price for variant '${v.variantId}' must be greater than zero.`);
        return;
      }
      if (v.discountPrice) {
        const dp = parseFloat(v.discountPrice);
        if (isNaN(dp) || dp < 0) {
          toast.error(`Discount price for variant '${v.variantId}' cannot be negative.`);
          return;
        }
        if (dp > p) {
          toast.error(`Discount price for variant '${v.variantId}' cannot exceed the base price.`);
          return;
        }
      }
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const payload = {
        storeId: selectedStoreId,
        productId: modalProduct.productId || modalProduct.id,
        enabled: enabled,
        featured: featured,
        recommended: recommended,
        isNew: isNew,
        displayOrder: parseInt(displayOrder) || 0,
        variants: variantsForm.map(v => ({
          variantId: v.variantId,
          price: parseFloat(v.price),
          discountPrice: v.discountPrice ? parseFloat(v.discountPrice) : null,
          availability: v.availability,
          sku: v.sku || null,
          image: v.image || null,
          calories: v.calories !== "" && v.calories !== null && v.calories !== undefined ? parseFloat(v.calories) : null,
          stockQuantity: v.stockQuantity !== "" && v.stockQuantity !== null && v.stockQuantity !== undefined ? parseFloat(v.stockQuantity) : null
        }))
      };

      if (isEditing) {
        await UpdateStoreProduct(token, payload);
        toast.success("Store product configuration updated successfully");
      } else {
        await AssignProductToStore(token, payload);
        toast.success("Product successfully assigned to store");
      }

      toggleModal();
      loadMenuAndCatalog();
    } catch (err) {
      console.error("Save store product configuration error:", err.response?.data || err);
      const errorMsg = err.response?.data?.error?.message || err.response?.data?.error || "Error occurred while saving configuration";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const toggleCloneModal = () => {
    if (cloneModal) {
      setCloneSourceStoreId("");
      setCloneResult(null);
    }
    setCloneModal(!cloneModal);
  };

  const handleCloneStoreMenu = async () => {
    if (!cloneSourceStoreId) {
      toast.error("Please select a store to clone from");
      return;
    }
    if (!selectedStoreId) {
      toast.error("Please select a target store first");
      return;
    }
    try {
      setCloneLoading(true);
      setCloneResult(null);
      const token = localStorage.getItem("token");
      const resp = await CloneStoreMenu(token, cloneSourceStoreId, selectedStoreId);
      const result = resp.data.data || resp.data;
      setCloneResult(result);
      if (result.cloned > 0) {
        toast.success(`Cloned ${result.cloned} product(s) to this store.`);
        loadMenuAndCatalog();
      } else if (result.skipped === result.total && result.total > 0) {
        toast.info("Every product from that store is already assigned here — nothing to clone.");
      }
    } catch (err) {
      console.error("Clone store menu error:", err);
      const errorMsg = err.response?.data?.error?.message || err.response?.data?.error || "Failed to clone store menu";
      toast.error(errorMsg);
    } finally {
      setCloneLoading(false);
    }
  };

  const handleRemoveClick = (row) => {
    setTargetProduct(row);
    setConfirmModal(true);
  };

  const confirmRemove = async () => {
    setConfirmModal(false);
    if (!targetProduct) return;
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      await RemoveProductFromStore(token, selectedStoreId, targetProduct.productId);
      toast.success("Product successfully removed from this store menu");
      loadMenuAndCatalog();
    } catch (err) {
      console.error(err);
      toast.error("Error occurred while removing product from store");
    } finally {
      setLoading(false);
      setTargetProduct(null);
    }
  };

  const getCategoryName = (id) => {
    const cat = categories.find(c => (c.categoryId || c.id) === id);
    return cat ? cat.category : id;
  };

  // Filter assigned list in-memory for live search (API does not filter search in-memory)
  const getFilteredAssigned = () => {
    let filtered = [...assignedMenu];
    if (assignedSearch) {
      const searchLower = assignedSearch.toLowerCase();
      filtered = filtered.filter(item =>
        item.productDetails?.name?.toLowerCase().includes(searchLower)
      );
    }
    if (assignedCategory) {
      filtered = filtered.filter(item => item.productDetails?.categoryId === assignedCategory);
    }
    if (assignedVeg) {
      const isVeg = assignedVeg === "true";
      filtered = filtered.filter(item => item.productDetails?.veg === isVeg);
    }
    return filtered;
  };

  const isAssigned = (productId) => {
    return assignedMenu.some(item => item.productId === productId);
  };

  // Columns for Catalog Products Table
  const catalogColumns = [
    {
      name: <span className="font-weight-bold fs-13">Image</span>,
      cell: (row) => (
        row.image ? (
          <img src={row.image} alt={row.name} width="50" height="50" style={{ objectFit: "cover", borderRadius: "4px" }} />
        ) : (
          <div className="bg-light d-flex align-items-center justify-content-center" style={{ width: 50, height: 50, borderRadius: "4px" }}>
            <i className="mdi mdi-pizza-thin text-muted" style={{ fontSize: "20px" }}></i>
          </div>
        )
      ),
      width: "80px",
    },
    {
      name: <span className="font-weight-bold fs-13">Product Name</span>,
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Category</span>,
      selector: (row) => getCategoryName(row.categoryId),
      sortable: true,
      width: "130px",
    },
    {
      name: <span className="font-weight-bold fs-13">Veg</span>,
      cell: (row) => (
        row.veg ? (
          <Badge color="soft-success" className="badge-soft-success">Veg</Badge>
        ) : (
          <Badge color="soft-danger" className="badge-soft-danger">Non-Veg</Badge>
        )
      ),
      width: "90px",
    },
    {
      name: <span className="font-weight-bold fs-13">Variants Type</span>,
      selector: (row) => row.variantEnabled ? (row.variantTypeId || "Yes") : "None",
      width: "120px",
    },
    {
      name: <span className="font-weight-bold fs-13">Action</span>,
      width: "160px",
      cell: (row) => {
        const assigned = isAssigned(row.productId || row.id);
        return (
          <button
            className={classnames("btn btn-sm", {
              "btn-success disabled": assigned,
              "btn-primary": !assigned
            })}
            onClick={() => !assigned && startAssign(row)}
            disabled={assigned}
          >
            {assigned ? "Assigned" : "Assign to Store"}
          </button>
        );
      },
    },
  ];
  const buttonText = isEditing ? "Save Changes" : "Assign to Store";

  return (
    <React.Fragment>
      <LoadingSpinner loading={loading} />
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Store" breadcrumbItem="Store Menu" />

          {/* Store Selection & Warning Bar */}
          <Card className="mb-4">
            <CardBody>
              <Row className="align-items-center">
                <Col md="4">
                  <FormGroup className="mb-0">
                    <Label className="font-weight-bold">Select Active Store Menu</Label>
                    {userRole === "superadmin" ? (
                      <Input
                        type="select"
                        value={selectedStoreId}
                        onChange={(e) => setSelectedStoreId(e.target.value)}
                      >
                        <option value="">Select Store</option>
                        {stores.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.city} - {s.name}
                          </option>
                        ))}
                      </Input>
                    ) : (
                      <div className="p-2 border rounded bg-light font-weight-semibold">
                        <i className="mdi mdi-store-outline me-2 text-primary"></i>
                        {userStoreName || "Your Scoped Store"}
                      </div>
                    )}
                  </FormGroup>
                </Col>

                {userRole === "admin" && (
                  <Col md="8" className="mt-3 mt-md-0">
                    <Alert color="info" className="mb-0 d-flex align-items-center gap-2">
                      <i className="mdi mdi-information-outline fs-16"></i>
                      <span>
                        Product not available? Contact Super Admin to add it into Product Catalog.
                      </span>
                    </Alert>
                  </Col>
                )}

                {userRole === "superadmin" && (
                  <Col md="8" className="mt-3 mt-md-0 text-md-end">
                    <Button
                      color="primary"
                      outline
                      onClick={toggleCloneModal}
                      disabled={!selectedStoreId}
                    >
                      <i className="mdi mdi-content-copy me-1"></i>
                      Clone Menu from Another Store
                    </Button>
                  </Col>
                )}
              </Row>
            </CardBody>
          </Card>

          {/* Tab Navigation */}
          <Nav tabs className="nav-tabs-custom mb-3">
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "1" })}
                onClick={() => toggleTab("1")}
                style={{ cursor: "pointer" }}
              >
                Assigned Store Menu
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === "2" })}
                onClick={() => toggleTab("2")}
                style={{ cursor: "pointer" }}
              >
                Assign Products (Catalog)
              </NavLink>
            </NavItem>
          </Nav>

          {/* Tab Contents */}
          <TabContent activeTab={activeTab}>
            {/* Assigned Menu Tab */}
            <TabPane tabId="1">
              <Card className="mb-3">
                <CardBody className="py-2">
                  <Row className="g-3 align-items-center">
                    <Col md="4">
                      <FormGroup className="mb-0">
                        <Input
                          type="text"
                          placeholder="Search assigned products..."
                          value={assignedSearch}
                          onChange={(e) => setAssignedSearch(e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup className="mb-0">
                        <Input
                          type="select"
                          value={assignedCategory}
                          onChange={(e) => setAssignedCategory(e.target.value)}
                        >
                          <option value="">All Categories</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.categoryId || cat.id}>
                              {cat.category}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup className="mb-0">
                        <Input
                          type="select"
                          value={assignedVeg}
                          onChange={(e) => setAssignedVeg(e.target.value)}
                        >
                          <option value="">All Types</option>
                          <option value="true">Veg</option>
                          <option value="false">Non-Veg</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="2">
                      <Button color="primary" className="w-100" onClick={handleSearchAssigned}>
                        Search
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
              {getFilteredAssigned().length === 0 ? (
                <div className="text-center p-5 bg-white rounded border">
                  <i className="mdi mdi-pizza-thin text-muted display-4 mb-3 d-block"></i>
                  <h5>No products assigned to this store yet.</h5>
                  <p className="text-muted">Go to the "Assign Products (Catalog)" tab to add products.</p>
                </div>
              ) : (
                <div className="table-responsive bg-white rounded border shadow-sm">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <table className="table table-hover table-striped align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: "80px" }} className="text-center font-weight-bold fs-13">Reorder</th>
                          <th style={{ width: "60px" }} className="text-center font-weight-bold fs-13">#</th>
                          <th style={{ width: "80px" }} className="font-weight-bold fs-13">Image</th>
                          <th className="font-weight-bold fs-13">Product Name</th>
                          <th style={{ width: "120px" }} className="font-weight-bold fs-13">Category</th>
                          <th style={{ width: "250px" }} className="font-weight-bold fs-13">Variants & Prices</th>
                          <th style={{ width: "90px" }} className="font-weight-bold fs-13">Featured</th>
                          <th style={{ width: "90px" }} className="font-weight-bold fs-13">Status</th>
                          <th style={{ width: "150px" }} className="font-weight-bold fs-13">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        <SortableContext
                          items={getFilteredAssigned().map((item) => item.productId)}
                          strategy={verticalListSortingStrategy}
                        >
                          {getFilteredAssigned().map((row, idx) => (
                            <SortableRow
                              key={row.productId}
                              row={row}
                              index={idx}
                              totalLength={getFilteredAssigned().length}
                              onEdit={startEdit}
                              onRemove={handleRemoveClick}
                              onMoveUp={handleMoveUp}
                              onMoveDown={handleMoveDown}
                              getCategoryName={getCategoryName}
                              isFiltered={!!(assignedSearch || assignedCategory || assignedVeg)}
                            />
                          ))}
                        </SortableContext>
                      </tbody>
                    </table>
                  </DndContext>
                  {!!(assignedSearch || assignedCategory || assignedVeg) && (
                    <div className="p-2 bg-soft-warning text-warning text-center border-top fs-12">
                      <i className="mdi mdi-alert-circle-outline me-1"></i>
                      Drag-and-drop reordering is disabled while search filters are active. Clear filters to reorder menu items.
                    </div>
                  )}
                </div>
              )}
            </TabPane>

            {/* Product Catalog Tab */}
            <TabPane tabId="2">
              <Card className="mb-3">
                <CardBody className="py-2">
                  <Row className="g-3 align-items-center">
                    <Col md="4">
                      <FormGroup className="mb-0">
                        <Input
                          type="text"
                          placeholder="Search catalog products..."
                          value={catalogSearch}
                          onChange={(e) => setCatalogSearch(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && handleSearchCatalog()}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup className="mb-0">
                        <Input
                          type="select"
                          value={catalogCategory}
                          onChange={(e) => setCatalogCategory(e.target.value)}
                        >
                          <option value="">All Categories</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.categoryId || cat.id}>
                              {cat.category}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup className="mb-0">
                        <Input
                          type="select"
                          value={catalogVeg}
                          onChange={(e) => setCatalogVeg(e.target.value)}
                        >
                          <option value="">All Types</option>
                          <option value="true">Veg</option>
                          <option value="false">Non-Veg</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="2">
                      <Button color="primary" className="w-100" onClick={handleSearchCatalog}>
                        Search
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
              <AlternativePagination columns={catalogColumns} data={catalogProducts} />
            </TabPane>
          </TabContent>
        </Container>
      </div>

      {/* Assignment / Edit Modal */}
      <Modal isOpen={modal} toggle={toggleModal} size="lg" centered>
        <ModalHeader toggle={toggleModal}>
          {isEditing ? "Edit Menu Configuration" : "Assign Product to Store Menu"}
        </ModalHeader>
        <ModalBody className="modal-color">
          {modalProduct && (
            <React.Fragment>
              <Row className="mb-3 border-bottom pb-3">
                <Col md="3">
                  {modalProduct.image ? (
                    <img src={modalProduct.image} alt={modalProduct.name} className="img-thumbnail w-100" style={{ maxHeight: "100px", objectFit: "cover" }} />
                  ) : (
                    <div className="bg-light d-flex align-items-center justify-content-center border rounded" style={{ height: "100px" }}>
                      <i className="mdi mdi-pizza-thin text-muted" style={{ fontSize: "36px" }}></i>
                    </div>
                  )}
                </Col>
                <Col md="9">
                  <h4 className="font-size-16 mb-1 text-primary">{modalProduct.name}</h4>
                  <p className="text-muted font-size-13 mb-1">{modalProduct.description || "No description provided."}</p>
                  <Badge color="soft-secondary" className="badge-soft-secondary">
                    {modalProduct.variantEnabled ? `Variant Enabled (${modalProduct.variantValueIds?.join(", ")})` : "Standard Product (No Variants)"}
                  </Badge>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="isEnabled">Active Status</Label>
                    <Input
                      id="isEnabled"
                      type="select"
                      value={enabled.toString()}
                      onChange={(e) => setEnabled(e.target.value === "true")}
                    >
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="isFeatured">Show in Best Seller?</Label>
                    <Input
                      id="isFeatured"
                      type="select"
                      value={featured.toString()}
                      onChange={(e) => setFeatured(e.target.value === "true")}
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="isRecommended">Show as Recommended?</Label>
                    <Input
                      id="isRecommended"
                      type="select"
                      value={recommended.toString()}
                      onChange={(e) => setRecommended(e.target.value === "true")}
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="isNewItem">Show "New" badge?</Label>
                    <Input
                      id="isNewItem"
                      type="select"
                      value={isNew.toString()}
                      onChange={(e) => setIsNew(e.target.value === "true")}
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <div className="d-flex align-items-end gap-2 mt-4 mb-2 p-3 bg-light bg-opacity-50 rounded border">
                <FormGroup className="mb-0 flex-grow-1">
                  <Label className="font-size-11 text-muted mb-1">
                    Copy price/discount/SKU/calories/stock from another store
                  </Label>
                  <Input
                    type="select"
                    value={copySourceStoreId}
                    onChange={(e) => setCopySourceStoreId(e.target.value)}
                  >
                    <option value="">Select a store to copy from...</option>
                    {stores
                      .filter((s) => s.id !== selectedStoreId)
                      .map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.city} - {s.name}
                        </option>
                      ))}
                  </Input>
                </FormGroup>
                <Button
                  color="secondary"
                  outline
                  onClick={handleCopyFromStore}
                  disabled={copyLoading || !copySourceStoreId}
                >
                  {copyLoading ? "Copying..." : "Copy"}
                </Button>
              </div>

              <h5 className="font-size-14 mt-4 mb-3 border-bottom pb-2">Pricing & Availability Configuration</h5>

              {variantsForm.map((v, idx) => (
                <div key={idx} className="border rounded p-3 mb-3 bg-light bg-opacity-25">
                  <div className="d-flex align-items-center mb-3">
                    <span className="badge bg-primary font-size-13 px-3 py-2">{v.variantId}</span>
                  </div>

                  <Row className="g-3">
                    <Col md="4">
                      <Label className="font-size-11 text-muted mb-1">Base Price (₹) *</Label>
                      <Input
                        type="number"
                        placeholder="e.g. 299"
                        value={v.price}
                        onChange={(e) => handleVariantPriceChange(idx, "price", e.target.value)}
                        required
                      />
                    </Col>
                    <Col md="4">
                      <Label className="font-size-11 text-muted mb-1">Discount Price (₹)</Label>
                      <Input
                        type="number"
                        placeholder="e.g. 249"
                        value={v.discountPrice}
                        onChange={(e) => handleVariantPriceChange(idx, "discountPrice", e.target.value)}
                      />
                    </Col>
                    <Col md="4">
                      <Label className="font-size-11 text-muted mb-1">Availability</Label>
                      <Input
                        type="select"
                        value={v.availability}
                        onChange={(e) => handleVariantPriceChange(idx, "availability", e.target.value)}
                      >
                        <option value="available">Available (In Stock)</option>
                        <option value="out_of_stock">Out of stock</option>
                        <option value="temporarily_unavailable">Temporarily unavailable</option>
                      </Input>
                    </Col>
                  </Row>

                  <Row className="g-3 mt-1">
                    <Col md="4">
                      <Label className="font-size-11 text-muted mb-1">SKU</Label>
                      <Input
                        type="text"
                        placeholder="SKU-001"
                        value={v.sku}
                        onChange={(e) => handleVariantPriceChange(idx, "sku", e.target.value)}
                      />
                    </Col>
                    <Col md="4">
                      <Label className="font-size-11 text-muted mb-1">Calories</Label>
                      <Input
                        type="number"
                        placeholder="e.g. 450"
                        value={v.calories}
                        onChange={(e) => handleVariantPriceChange(idx, "calories", e.target.value)}
                      />
                    </Col>
                    <Col md="4">
                      <Label className="font-size-11 text-muted mb-1">Stock Qty</Label>
                      <Input
                        type="number"
                        placeholder="Blank = unlimited"
                        min="0"
                        value={v.stockQuantity}
                        onChange={(e) => handleVariantPriceChange(idx, "stockQuantity", e.target.value)}
                      />
                    </Col>
                  </Row>

                  <Row className="mt-3">
                    <Col md="12">
                      <Label className="font-size-11 text-muted mb-1">Variant-specific image (optional — falls back to the product image)</Label>
                      <AWSS3Uploader
                        onUploadComplete={(url) => handleVariantPriceChange(idx, "image", url)}
                        folder="variants"
                      />
                      {v.image ? (
                        <img src={v.image} alt={v.variantId} width="48" height="48" className="mt-2 rounded" />
                      ) : null}
                    </Col>
                  </Row>
                </div>
              ))}
            </React.Fragment>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSave}>
            {buttonText}
          </Button>{" "}
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Remove Confirmation Modal */}
      <Modal isOpen={confirmModal} toggle={() => setConfirmModal(!confirmModal)} centered>
        <ModalHeader toggle={() => setConfirmModal(!confirmModal)}>Confirm Removal</ModalHeader>
        <ModalBody>
          Are you sure you want to remove this product from the store menu? This action cannot be undone.
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={confirmRemove}>Remove</Button>{' '}
          <Button color="secondary" onClick={() => setConfirmModal(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>

      {/* Clone Entire Store Menu Modal */}
      <Modal isOpen={cloneModal} toggle={toggleCloneModal} centered>
        <ModalHeader toggle={toggleCloneModal}>Clone Menu from Another Store</ModalHeader>
        <ModalBody>
          <p className="text-muted">
            This copies every product currently assigned to the source store —
            with the same price, discount, SKU, calories and stock config —
            into{" "}
            <strong>
              {stores.find((s) => s.id === selectedStoreId)?.name || "the selected store"}
            </strong>
            . Products already assigned there are left untouched, not
            overwritten.
          </p>
          <FormGroup>
            <Label className="font-weight-bold">Copy menu from</Label>
            <Input
              type="select"
              value={cloneSourceStoreId}
              onChange={(e) => setCloneSourceStoreId(e.target.value)}
            >
              <option value="">Select source store...</option>
              {stores
                .filter((s) => s.id !== selectedStoreId)
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.city} - {s.name}
                  </option>
                ))}
            </Input>
          </FormGroup>

          {cloneResult && (
            <Alert color={cloneResult.errors?.length ? "warning" : "success"} className="mb-0">
              <div>Total on source store: {cloneResult.total}</div>
              <div>Newly cloned: {cloneResult.cloned}</div>
              <div>Already assigned (skipped): {cloneResult.skipped}</div>
              {cloneResult.errors?.length > 0 && (
                <div className="mt-2">
                  <strong>{cloneResult.errors.length} item(s) failed:</strong>
                  <ul className="mb-0">
                    {cloneResult.errors.map((e, i) => (
                      <li key={i} className="fs-12">{e.productId}: {e.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={handleCloneStoreMenu}
            disabled={cloneLoading || !cloneSourceStoreId}
          >
            {cloneLoading ? "Cloning..." : "Clone Menu"}
          </Button>{" "}
          <Button color="secondary" onClick={toggleCloneModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default StoreMenu;
